import React, { useState } from "react";
import {
  Box,
  VStack,
  Heading,
  Text,
  Button,
  Wrap,
  WrapItem,
  Collapse,
  Icon,
  HStack,
  Flex,
  Link,
  SimpleGrid,
  Image,
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { FaFileAlt } from "react-icons/fa";

const PublicationLinks = ({ publications }) => (
  <HStack spacing={1} ml={2}>
    {publications.map((url, index) => (
      <Link key={index} href={url} isExternal>
        <Icon
          as={FaFileAlt}
          color="brand.600"
          boxSize={4}
          _hover={{ color: "brand.700" }}
        />
      </Link>
    ))}
  </HStack>
);

const GeneButton = ({ gene, inProgress, disease, columnIndex }) => {
  const diseaseMatch = disease.match(/\(([^)]+)\)/);
  const baseDiseaseSlug = diseaseMatch ? diseaseMatch[1] : disease;
  const prefix =
    columnIndex === 0 ? "GP2" : columnIndex === 1 ? "MDSGENE" : "";
  const isObjectGene = typeof gene === "object" && gene !== null;
  const publications = isObjectGene ? gene.publications : undefined;
  const geneName = isObjectGene ? gene.name : gene;
  const label = isObjectGene ? gene.label || geneName : geneName;
  const subLabel = isObjectGene ? gene.subLabel : undefined;
  const hrefValue = isObjectGene ? gene.href : undefined;
  const isNotAffected =
    (typeof subLabel === "string" && subLabel.toLowerCase() === "not affected") ||
    (typeof hrefValue === "string" && hrefValue.toUpperCase().startsWith("NOPARK-"));
  const diseaseSlugSuffix = isNotAffected ? "NOPARK" : baseDiseaseSlug;
  const diseaseSlug = prefix ? `${prefix}${diseaseSlugSuffix}` : diseaseSlugSuffix;
  const slug = isObjectGene ? gene.slug || geneName : geneName;
  const defaultHref = `/genes/${diseaseSlug}-${slug}`;
  const resolveHref = () => {
    if (!isObjectGene || !hrefValue) {
      return defaultHref;
    }
    if (typeof hrefValue === "string") {
      if (hrefValue.startsWith('/')) {
        return hrefValue;
      }
      if (columnIndex === 0 || columnIndex === 1) {
        return defaultHref;
      }
      return `/genes/${hrefValue}`;
    }
    if (typeof hrefValue === "object" && hrefValue !== null) {
      const perColumnHref = hrefValue[columnIndex];
      if (typeof perColumnHref === "string") {
        return perColumnHref.startsWith('/')
          ? perColumnHref
          : `/genes/${perColumnHref}`;
      }
    }
    return defaultHref;
  };
  const resolvedHref = resolveHref();
  return (
    <WrapItem>
      <Button
        as="a"
        href={resolvedHref}
        size="sm"
        variant="outline"
        position="relative"
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap={0}
      >
        <Text>{label}</Text>
        {subLabel && (
          <Text fontSize="xs" color="neutral.600" lineHeight="short">
            {subLabel}
          </Text>
        )}
        {inProgress && (
          <Text
            position="absolute"
            bottom="-20px"
            left="50%"
            transform="translateX(-50%)"
            fontSize="xs"
            color="fuchsia.500"
            whiteSpace="nowrap"
          >
            in progress
          </Text>
        )}
      </Button>
      {publications?.map((url, index) => (
        <Link key={index} href={url} isExternal>
          <Icon
            as={FaFileAlt}
            color="brand.600"
            boxSize={4}
            _hover={{ color: "brand.700" }}
          />
        </Link>
      ))}
    </WrapItem>
  );
};
const SubIllnessSection = ({
  name,
  genes,
  publications,
  inProgress,
  disease,
  columnIndex,
}) => (
  <Box mb={4}>
    {name && (
      <HStack mb={2}>
        <Text fontWeight="bold">{name}</Text>
        {publications && <PublicationLinks publications={publications} />}
      </HStack>
    )}
    <Wrap spacing={2}>
      {genes?.length > 0 ? (
        genes.map((gene, index) => (
          <GeneButton
            key={index}
            disease={disease}
            gene={gene}
            inProgress={inProgress}
            columnIndex={columnIndex}
          />
        ))
      ) : (
        <Text fontSize="sm" color="neutral.500">
          No genes available
        </Text>
      )}
    </Wrap>
  </Box>
);
const ColumnHeader = ({ logoSrc, logoAlt, title }) => {
  const isMergedData = title === "Merged Data" && !logoSrc;
  const titleStyles = isMergedData
    ? { fontSize: { base: "lg", md: "xl" }, fontWeight: "bold", color: "brand.600" }
    : { fontSize: "md", fontWeight: "semibold" };

  return (
    <VStack spacing={2} align="center" mb={2}>
      {logoSrc && (
        <Image
          src={logoSrc}
          alt={logoAlt || title || ""}
          w={{ base: "60%", md: "70%" }}
          maxW="220px"
          objectFit="contain"
        />
      )}
      {title && (
        <Text textAlign="center" {...titleStyles}>
          {title}
        </Text>
      )}
    </VStack>
  );
};
const DiseaseSection = ({ title, content, columnIndex }) => {
  const [isOpen, setIsOpen] = useState(true);
  return (
    <Box bg="neutral.100" p={4} borderRadius="md">
      <Flex
        justify="space-between"
        align="center"
        onClick={() => setIsOpen(!isOpen)}
        cursor="pointer"
      >
        <Heading as="h3" size="md">
          {title}
        </Heading>
        <Icon as={isOpen ? ChevronUpIcon : ChevronDownIcon} />
      </Flex>
      <Collapse in={isOpen}>
        <VStack align="stretch" mt={4} spacing={4}>
          {Array.isArray(content) ? (
            content.map((subIllness, index) => (
              <SubIllnessSection
                disease={title}
                key={index}
                columnIndex={columnIndex}
                {...subIllness}
              />
            ))
          ) : (
            <SubIllnessSection
              disease={title}
              columnIndex={columnIndex}
              {...content}
            />
          )}
        </VStack>
      </Collapse>
    </Box>
  );
};
const columnConfigs = [
  {
    columnIndex: 0,
    headerProps: {
      logoSrc: "/logo1.svg",
      logoAlt: "GP2 Global Parkinson's Genetics Program logo",
    },
  },
  {
    columnIndex: 1,
    headerProps: {
      logoSrc: "/logo1.png",
      logoAlt: "MDSGene logo",
    },
  },
  {
    columnIndex: 2,
    headerProps: {
      title: "Merged Data",
    },
    spacerHeight: { base: 0, lg: "0px" },
  },
];
const Genes = () => {
  return (
    <VStack spacing={6} align="stretch">
      <Text fontSize="xl" fontWeight="bold" color="fuchsia.500">
        Select a gene for your disease of interest:
      </Text>
      <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={4} alignItems="start">
        {columnConfigs.map(({ columnIndex, headerProps, spacerHeight }) => (
          <VStack key={columnIndex} spacing={4} align="stretch">
            {spacerHeight && (
              <Box h={spacerHeight} aria-hidden="true" />
            )}
            <ColumnHeader {...headerProps} />
            {Object.entries(dataByColumn[columnIndex] || {}).map(([disease, content], index) => (
              <DiseaseSection
                key={`${disease}-${columnIndex}-${index}`}
                title={disease}
                content={content}
                columnIndex={columnIndex}
              />
            ))}
          </VStack>
        ))}
      </SimpleGrid>
    </VStack>
  );
};
export default Genes;
const parkinsonismSectionsBySource = [
  [
    {
      name: "Classical parkinsonism, dominant forms",
      publications: ["https://pubmed.ncbi.nlm.nih.gov/example5/"],
      genes: [
        "LRRK2",
        { name: "LRRK2", label: "LRRK2", subLabel: "not affected", href: "NOPARK-LRRK2" },
      ],
    },
    {
      name: "Early-onset parkinsonism, recessive forms",
      publications: ["https://pubmed.ncbi.nlm.nih.gov/example6/"],
      genes: ["PRKN", "PINK1"],
    },
    {
      name: "Atypical parkinsonism",
      publications: ["https://pubmed.ncbi.nlm.nih.gov/example7/"],
      genes: [],
    },
  ],
  [
    {
      name: "Classical parkinsonism, dominant forms",
      publications: ["https://pubmed.ncbi.nlm.nih.gov/example5/"],
      genes: [
        "LRRK2",
        { name: "LRRK2", label: "LRRK2", subLabel: "not affected", href: "NOPARK-LRRK2" },
      ],
    },
    {
      name: "Early-onset parkinsonism, recessive forms",
      publications: ["https://pubmed.ncbi.nlm.nih.gov/example6/"],
      genes: ["PRKN", "PINK1"],
    },
    {
      name: "Atypical parkinsonism",
      publications: ["https://pubmed.ncbi.nlm.nih.gov/example7/"],
      genes: [],
    },
  ],
  [
    {
      name: "Classical parkinsonism, dominant forms",
      publications: ["https://pubmed.ncbi.nlm.nih.gov/example5/"],
      genes: [
        "LRRK2",
        { name: "LRRK2", label: "LRRK2", subLabel: "not affected", href: "NOPARK-LRRK2" },
      ],
    },
    {
      name: "Early-onset parkinsonism, recessive forms",
      publications: ["https://pubmed.ncbi.nlm.nih.gov/example6/"],
      genes: ["PRKN", "PINK1"],
    },
    {
      name: "Atypical parkinsonism",
      publications: ["https://pubmed.ncbi.nlm.nih.gov/example7/"],
      genes: [],
    },
  ],
];

const dataByColumn = parkinsonismSectionsBySource.map((sections) => ({
  "Parkinsonism (PARK)": sections,
}));
